import styles from './User.module.css';

import { styled, useTheme } from '@mui/material/styles';

import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Edit from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import Check from '@mui/icons-material/Check';
import CloudUpload from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Inventory from '@mui/icons-material/Inventory';
import Storefront from '@mui/icons-material/Storefront';

import React from "react";

import * as api from "../../api.js";

import { useAuth } from '../../AuthContext';
import { Items } from '../Items/Items';
import { generateItemLabels } from '../../utils/labelUtils';

export const User = ({ user, onSave }) => {
    const [ error, setError ] = React.useState(null);
    const [ loading, setLoading ] = React.useState(false);
    const [ localUser, setLocalUser ] = React.useState(user);
    const [ requiredFields, setRequiredFields ] = React.useState([]);
    const [ editable, setEditable ] = React.useState(false);
    const [ editAction, setEditAction ] = React.useState('cancel');
    const [ phoneIsValid, setPhoneIsValid ] = React.useState(false);
    const [ inventoryDialogOpen, setInventoryDialogOpen ] = React.useState(false);
    const [ userItems, setUserItems ] = React.useState([]);
    const [ selectedFiles, setSelectedFiles ] = React.useState([]);
    const [ importProgress, setImportProgress ] = React.useState(null);
    const [ importStatus, setImportStatus ] = React.useState('idle'); // 'idle', 'running', 'paused', 'stopped'
    const [ importController, setImportController ] = React.useState(null); // To control the import process
    const [ successMessage, setSuccessMessage ] = React.useState('');
    const [ selectedItems, setSelectedItems ] = React.useState([]);
    const [ importCount, setImportCount ] = React.useState(0);
    const [ verifying, setVerifying ] = React.useState(false);

    const { auth, claims, token } = useAuth();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const importStatusRef = React.useRef('idle'); // Synchronous ref for pause checking

    const userIsSelf = auth.currentUser.uid === localUser.uid;

    const isNewItem = (id) => /^new-\d+(-[a-zA-Z0-9]+)?$/.test(id);

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const getCategory = (categoryName) => {
        const categories = {
            'Bibs': '4',
            'Cross Country Boots': '3',
            'Cross Country Skis': '1',
            'Goggles': '5',
            'Hats': '4',
            'Helmets': '5',
            'Jacket': '4',
            'Other': '0',
            'Pants': '4',
            'Ski Bag': '7',
            'Ski Boots': '3',
            'Ski Poles': '2',
            'Skis': '1',
            'Snowboard': '1',
            'Snowboard Boots': '3',
            'Socks': '3',
            'Thermals': '4'
        };

        return categories[ categoryName ] || '0';
    };

    // MARK: validate phone number
    const validatePhoneNumber = (phoneNumber) => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Phone number is required.');

            setPhoneIsValid(false);

            return false;
        }

        const phonePlusOne = /^\+1/.test(phoneNumber);
        const fullNumber = phonePlusOne ? phoneNumber : `+1${ phoneNumber }`;
        const isValid = /^(\+1)\d{10}$/.test(fullNumber);

        if (isValid) {
            setRequiredFields(prev => prev.filter(field => field !== 'phoneNumber'));
            setError(null);
            setLocalUser(prev => ({ ...prev, phoneNumber: fullNumber }));

            setPhoneIsValid(true);

            return true;
        } else {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Invalid phone number format. Must be 10 digits.');

            setPhoneIsValid(false);

            return false;
        }
    };

    // MARK: Import handler
    const handleImport = async () => {
        if (selectedFiles.length === 0) {
            setError('No files selected for import.');

            return;
        }

        // Create an AbortController for this import session
        const controller = new AbortController();

        setImportController(controller);
        setImportStatus('running');

        importStatusRef.current = 'running';

        try {
            // get file
            const file = selectedFiles[ 0 ];
            const textData = await file.text();
            const jsonData = JSON.parse(textData);

            // Transform items for import
            const transformedItems = jsonData.map(item => ({
                seller: localUser.uid,
                name: item.model,
                brand: item.brand,
                category: getCategory(item.category),
                desc: item.category,
                condition: 'New',
                size: item.size,
                quantity: item.qty,
                price: item.sale ? parseFloat(item.sale).toFixed(2) : parseFloat(0).toFixed(2),
                gender: item.gender,
                barcode: item.tag,
                feePaid: true
            }));

            setImportProgress({ current: 0, total: jsonData.length, status: 'Importing...' });

            let successCount = 0;
            let failCount = 0;

            const errors = [];

            // Process items one at a time with delay to avoid overwhelming the server
            for (let i = 0; i < transformedItems.length; i++) {
                // Check if import was stopped
                if (controller.signal.aborted) {
                    setImportStatus('stopped');

                    importStatusRef.current = 'stopped';

                    setImportProgress(prev => ({ ...prev, status: 'Import stopped by user' }));

                    return;
                }

                // Check if import is paused
                while (importStatusRef.current === 'paused') {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    if (controller.signal.aborted) {
                        setImportStatus('stopped');

                        importStatusRef.current = 'stopped';

                        setImportProgress(prev => ({ ...prev, status: 'Import stopped by user' }));

                        return;
                    }
                }

                const item = transformedItems[ i ];

                try {
                    const result = await api.addItem(item, token);

                    if (result && !result.error) {
                        successCount++;
                    } else {
                        failCount++;

                        errors.push(`Item ${ i + 1 }: ${ result?.error || 'Unknown error' }`);
                    }
                } catch (error) {
                    failCount++;
                    errors.push(`Item ${ i + 1 }: ${ error.message }`);
                }

                // Update progress
                setImportProgress({
                    current: i + 1,
                    total: jsonData.length,
                    status: `Imported ${ i + 1 }/${ jsonData.length }...`
                });

                // Add delay between requests to prevent server overload (50ms = 20 requests/second)
                if (i < transformedItems.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
            }

            // Show final results
            if (failCount === 0) {
                setSuccessMessage(`Successfully imported ${ successCount } items`);
            } else if (successCount > 0) {
                setError(`Imported ${ successCount } items, ${ failCount } failed. Errors: ${ errors.slice(0, 5).join('; ') }${ errors.length > 5 ? `... and ${ errors.length - 5 } more` : '' }`);
            } else {
                setError(`Import failed. No items were imported. First error: ${ errors[ 0 ] || 'Unknown error' }`);
            }

            setImportProgress(null);
            setImportStatus('idle');
            importStatusRef.current = 'idle';
            setImportController(null);

        } catch (error) {
            if (error.name === 'AbortError') {
                setImportStatus('stopped');
                importStatusRef.current = 'stopped';
                setImportProgress(prev => ({ ...prev, status: 'Import stopped by user' }));
            } else {
                console.error('Import error:', error);
                setError(`Import failed: ${ error.message }`);
                setImportProgress(null);
                setImportStatus('idle');
                importStatusRef.current = 'idle';
                setImportController(null);
            }
        }
    };

    // MARK: Import control handlers
    const handlePauseImport = () => {
        setImportStatus('paused');

        importStatusRef.current = 'paused';

        setImportProgress(prev => ({ ...prev, status: 'Import paused' }));
    };

    const handleResumeImport = () => {
        setImportStatus('running');

        importStatusRef.current = 'running';

        setImportProgress(prev => ({ ...prev, status: 'Importing...' }));
    };

    const handleStopImport = () => {
        if (importController) {
            importController.abort();
        }

        setImportStatus('stopped');

        importStatusRef.current = 'stopped';
    };
    const handleSelectItem = (items) => {
        setSelectedItems(items);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(userItems);
        } else {
            setSelectedItems([]);
        }
    };

    const handleNewItem = () => {
        const newItem = {
            id: `new-${ Date.now() }`,
            name: '',
            brand: '',
            desc: '',
            size: '',
            color: '',
            gender: '',
            category: '',
            image: '',
            price: 0,
            quantity: 0,
            condition: ''
        };

        setUserItems(prev => [ ...prev, newItem ]);
    };

    const handleVerifySelected = async () => {
        if (selectedItems.length === 0) return;

        setVerifying(true);

        try {
            // Verify selected items
            const result = await api.verifyItems(selectedItems, localUser.uid, token);

            if (result.error) {
                setError(`Failed to verify items: ${ result.error }`);
                
                return;
            }

            setSuccessMessage(`Successfully verified ${ selectedItems.length } items`);

            await generateItemLabels(selectedItems);

            setSelectedItems([]);

            // Refresh the user items
            const updatedItems = await api.getItemsBySeller(localUser.uid, token);

            setUserItems(updatedItems || []);

        } catch (error) {
            setError(`Failed to verify items: ${ error.message }`);
        } finally {
            setVerifying(false);
        }
    };

    const handleSellerPayout = async () => {
        if (selectedItems.length === 0) return;

        try {
            // Payout selected items
            const result = await api.payoutSeller(selectedItems, localUser.uid, token);

            if (result.error) {
                return { error: result.error };
            }

            // Clear selections and refresh data
            setSelectedItems([]);

            const updatedItems = await api.getItemsBySeller(localUser.uid, token);

            setUserItems(updatedItems || []);

            return { results: true, payout: result.payout };
        } catch (error) {
            return { error: error.message };
        }
    };

    const handleSaveItem = async (item, photo) => {
        if (!item) return;

        try {
            setLoading(true);

            const ret = isNewItem(item.id) ? await api.addItem({ ...item, seller: user.id }, token) : await api.updateItem({ ...item, seller: user.id }, token);

            if (!ret?.error && photo) {
                const result = await api.uploadPhoto(ret, photo, token);

                if (result.error) {
                    setError(result.error);

                    return;
                }
            }

            if (ret?.error) {
                setError(ret.error);

                return;
            }

            if (isNewItem(item.id)) {
                setUserItems(prev => prev.map(i => i.id === item.id ? { ...item, id: ret } : i));
            } else {
                setUserItems(prev => prev.map(i => i.id === item.id ? item : i));
            }
        } catch (error) {
            setError('Failed to update item');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveItems = async (ids) => {
        const ret = ids && await api.deleteItems(ids, token);

        if (ret?.error) {
            setError(ret.error);
        } else {
            setUserItems(prev => prev.filter(i => !ids.includes(i.id)));

            setError(null);
        }
    };

    const handleOnChange = (e) => {
        const { name, value, type, checked, required } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setLocalUser(prev => ({ ...prev, [ name ]: fieldValue }));

        if (name === 'phoneNumber') {
            const isValid = validatePhoneNumber(fieldValue);

            if (!isValid) {
                return;
            }
        }

        // Handle required field validation
        if (required && !fieldValue) {
            setRequiredFields(prev => [ ...prev.filter(field => field !== name), name ]);
            setError(`${ name } is required.`);

            return;
        }

        // Clear field from required list if now valid
        setRequiredFields(prev => prev.filter(field => field !== name));
        setEditAction('save');
        setError(null);
    };

    const handleAdminChange = async (e) => {
        const { name, checked } = e.target;

        if (!userIsSelf) {
            const role = checked && name === 'admin' ? 'admin' : 'user';

            setLocalUser(prev => ({ ...prev, role, admin: checked, editor: false }));
            setEditAction('save');
        }
    };

    const handleEditorChange = async (e) => {
        const { name, checked } = e.target;

        if (!userIsSelf) {
            const role = checked && name === 'editor' ? 'editor' : 'user';

            setLocalUser(prev => ({ ...prev, role, admin: false, editor: checked }));
            setEditAction('save');
        }
    };

    const handleVerifyEmail = async (e) => {
        const { name, checked } = e.target;

        if (!userIsSelf && name === 'verifyEmail') {
            setLocalUser(prev => ({ ...prev, verifyEmail: checked }));
            setEditAction('save');
        }
    };

    // MARK: Edit handler
    const handleOnEdit = async () => {
        if (editable && editAction === 'save') {
            if (requiredFields.length > 0) {
                setError(`The following fields are required: ${ requiredFields.join(', ') }`);

                return;
            }

            const saved = await api.updateUser(localUser, token);

            if (saved.error) {
                setError(saved.error);
            } else {
                onSave && onSave(localUser);

                setError(null);
                setEditable(false);
            }
        } else if (editable && editAction === 'cancel') {
            setEditable(false);
        } else {
            setEditable(true);
            setEditAction('cancel');
        }
    };

    // MARK: Cancel handler
    const handleOnCancel = async () => {
        setEditable(false);
    };

    // MARK: Inventory handler
    const handleInventoryClick = async () => {
        const items = await api.getItemsBySeller(localUser.uid, token);
        if (items && !items.error) {
            setUserItems(items);
            setInventoryDialogOpen(true);
        }
    };

    // MARK: File upload handler
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    // MARK: admin & required effect
    React.useEffect(() => {
        const missing = [];

        if (!localUser.firstName) missing.push('firstName');
        if (!localUser.lastName) missing.push('lastName');
        if (!localUser.phoneNumber) missing.push('phoneNumber');

        setRequiredFields(missing);
        setError(missing.length > 0 ? `The following fields are required: ${ missing.join(', ') }` : null);
    }, [ localUser.firstName, localUser.lastName, localUser.phoneNumber ]);

    // MARK: Update edit effect
    React.useEffect(() => {
        setEditAction(editable ? 'cancel' : 'edit');
    }, [ editable ]);

    // MARK: Validate phone effect
    React.useEffect(() => {
        validatePhoneNumber(localUser.phoneNumber || '');
    }, [ localUser.phoneNumber ]);

    React.useEffect(() => {
        const getImportCount = async () => {
            const file = selectedFiles[ 0 ];
            const textData = await file.text();
            const jsonData = JSON.parse(textData);

            setImportCount(jsonData.length);
        };

        if (selectedFiles.length > 0) {
            getImportCount();
        }
    }, [ selectedFiles ]);

    // MARK: Render
    if (loading) {
        return <CircularProgress />;
    }

    return (
        <>
            <Accordion expanded={ editable } onChange={ handleOnEdit } sx={ {
                width: '100%',
                background: 'none',
                boxShadow: 'none',
                border: editable ? '2px solid #f0e356ff' : 'none', '&:before': { display: 'none' }
            } }>
                { editable ? (
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <IconButton 
                                onClick={handleOnEdit} 
                                size="small"
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <div className={ styles.form }>
                            {/* MARK: Personal Information */ }
                            <div className={ styles.formSection }>
                                <h3>Personal Information</h3>

                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <label htmlFor="firstName">First Name</label>

                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            autoComplete='given-name'
                                            required
                                            value={ localUser.firstName || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('firstName') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('firstName') && <Alert severity="error" variant='filled'>First name is required.</Alert> }
                                    </div>

                                    <div className={ styles.formField }>
                                        <label htmlFor="lastName">Last Name</label>

                                        <input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            autoComplete='family-name'
                                            required
                                            value={ localUser.lastName || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('lastName') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('lastName') && <Alert severity="error" variant='filled'>Last name is required.</Alert> }
                                    </div>
                                </div>

                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <label htmlFor="firstName">Vendor Name</label>

                                        <input
                                            type="text"
                                            id="vendorName"
                                            name="vendorName"
                                            autoComplete='organization'
                                            value={ localUser.vendorName || '' }
                                            onChange={ handleOnChange }
                                        />
                                    </div>

                                    <div className={ styles.formField }>
                                    </div>
                                </div>
                            </div>

                            {/* MARK: Contact Information */ }
                            <div className={ styles.formSection }>
                                <h3>Contact Information</h3>
                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <label htmlFor="phoneNumber">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            autoComplete='tel'
                                            required
                                            value={ localUser.phoneNumber || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('phoneNumber') ? styles.required : !phoneIsValid ? styles.required : '' }
                                        />

                                        { requiredFields.includes('phoneNumber') && <Alert severity="error" variant='filled'>Phone number is invalid.</Alert> }

                                    </div>

                                    <div className={ styles.formField }>
                                        <label htmlFor="email">Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            autoComplete='email'
                                            value={ localUser.email }
                                            readOnly={ true }
                                            className={ `${ styles.readOnly } ${ styles.verified }` }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* MARK: admin */ }
                            { !userIsSelf && <div className={ styles.formSection }>
                                <h3>Account Settings</h3>

                                <Card elevation={ 0 } sx={ { backgroundColor: 'rgba(0, 0, 0, 0.25)' } }>
                                    <Typography color='secondary.main' sx={ { p: 2, pb: 0 } }>Set this user as an admin</Typography>

                                    <CardContent sx={ { xs: { p: 0 }, sm: { p: 1 } } }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="admin"
                                                    checked={ !!localUser.admin }
                                                    onChange={ handleAdminChange }
                                                    color={ 'secondary.main' }
                                                />
                                            }
                                            label="Admin"
                                            sx={ {
                                                color: 'secondary.main',
                                                fontWeight: 600
                                            } }
                                        />

                                        <List sx={ { xs: { p: 0 }, sm: { ml: 6 } } }>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2" color='error'>- full access to all Swap functions</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- manage users and items</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- can operate POS</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- create and view all reports and analytics</Typography></ListItem>
                                        </List>
                                    </CardContent>
                                </Card>

                                <Divider sx={ { my: 2 } } />

                                <Card elevation={ 0 } sx={ { backgroundColor: 'rgba(0, 0, 0, 0.25)' } }>
                                    <Typography color='info.main' sx={ { p: 2, pb: 0 } }>Set this user as an editor</Typography>

                                    <CardContent sx={ { xs: { p: 0 }, sm: { p: 1 } } }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="editor"
                                                    checked={ !!localUser.editor }
                                                    disabled={ !!localUser.admin }
                                                    onChange={ handleEditorChange }
                                                    color={ 'info.main' }
                                                />
                                            }
                                            label="Editor"
                                            sx={ {
                                                color: 'info.main',
                                                fontWeight: 600
                                            } }
                                        />

                                        <List sx={ { xs: { p: 0 }, sm: { ml: 6 } } }>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2" color='success'>- CAN edit and verify items</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2" color='success'>- CAN edit users</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2" color='error'>- CANNOT operate POS</Typography></ListItem>
                                        </List>
                                    </CardContent>
                                </Card>

                                <Divider sx={ { my: 2 } } />

                                <Card elevation={ 0 } sx={ { backgroundColor: 'rgba(0, 0, 0, 0.25)' } }>
                                    <Typography sx={ { p: 2, pb: 0 } }>Manually Verify Email</Typography>

                                    <CardContent sx={ { xs: { p: 0 }, sm: { p: 1 } } }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="verifyEmail"
                                                    checked={ !!localUser.verifyEmail || localUser.emailVerified }
                                                    onChange={ handleVerifyEmail }
                                                    disabled={ localUser.emailVerified }
                                                    color={ localUser.emailVerified ? 'success' : 'primary' }
                                                />
                                            }
                                            label={ localUser.emailVerified ? "Email Verified" : "Verify Email" }
                                            sx={ {
                                                color: localUser.emailVerified ? 'success' : 'primary',
                                                fontWeight: 600,
                                                pb: 2
                                            } }
                                        />
                                    </CardContent>
                                </Card>

                                <Divider sx={ { my: 2 } } />

                                <Card elevation={ 0 } sx={ { p: 2, backgroundColor: 'rgba(0, 0, 0, 0.5)' } }>
                                    <Typography color="error" sx={ { mb: 2 } }>Disabling an account prevents all access to the Patrol360 platform.</Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="disabled"
                                                checked={ localUser.disabled || false }
                                                onChange={ handleOnChange }
                                                color={ localUser.disabled ? 'error' : 'default' }
                                            />
                                        }
                                        label="Disable Account"
                                        sx={ {
                                            color: '#f44336',
                                            fontWeight: 600
                                        } }
                                    />
                                </Card>

                            </div> }
                        </div>

                        {/* MARK: Actions */ }
                        <AccordionActions sx={ { mt: 2 } }>
                            <Button variant="outlined" color="warning" onClick={ handleOnCancel }>Cancel</Button>
                            <Button variant="contained" color="primary" onClick={ handleOnEdit } disabled={ (userIsSelf && (editAction === "cancel")) }>Save</Button>
                        </AccordionActions>

                        { (error || requiredFields.length > 0) && <Alert severity="error" variant='filled'>{ error || `The following fields are required: ${ requiredFields.join(', ') }` }</Alert> }
                        { successMessage && <Alert severity="success" variant='filled' onClose={ () => setSuccessMessage('') }>{ successMessage }</Alert> }
                    </AccordionDetails>
                ) : (
                    <AccordionSummary
                        expandIcon={ isMobile ? null : <Edit color="primary" /> }
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        sx={ { p: 0 } }
                    >
                        <div className={ `${ styles.info } ${ localUser?.disabled ? styles.disabled : '' }` }>
                            <Grid container alignItems="center" spacing={ 1 } sx={ { width: '100%' } }>
                                <Grid size={ 3 } className={ `${ !localUser?.displayName?.trim() ? styles.missing : '' }` }>
                                    <Tooltip title={ `${ localUser?.displayName?.trim() || 'No Display Name' }` }>
                                        <Typography component="div" noWrap>
                                            { `${ localUser?.displayName?.trim() || 'No Display Name' }` }
                                        </Typography>
                                    </Tooltip>
                                </Grid>

                                <Grid size={ 3 }>
                                    <Tooltip title={ localUser?.phoneNumber || 'Phone number not provided' }>
                                        <Typography component="div" noWrap>
                                            { localUser?.phoneNumber || 'Phone' }
                                        </Typography>
                                    </Tooltip>
                                </Grid>

                                <Grid size={ 3 } className={ `${ localUser?.emailVerified ? styles.verified : '' }` }>
                                    <Tooltip title={ localUser?.email || 'Email not provided' }>
                                        <Typography component="div" noWrap color={ localUser?.emailVerified ? 'success.main' : 'inherit' }>
                                            { localUser?.email || 'Email' }
                                            { localUser?.emailVerified && <Check fontSize='small' /> }
                                        </Typography>
                                    </Tooltip>
                                </Grid>

                                <Grid size={ 1 } textAlign='right'>
                                    <Tooltip title={ user?.vendorName ? `${ user?.vendorName }` : "User-provided item" }>
                                        <Storefront
                                            color={ user?.vendorName ? 'success' : 'disabled' }
                                            aria-label="verified"
                                            sx={ { fontSize: { xs: 16, sm: 24 } } }
                                        />
                                    </Tooltip>
                                </Grid>

                                <Grid size={ 2 } textAlign='center'>
                                    <Tooltip title={ `${ localUser?.inventoryCount || 0 } item${ (localUser?.inventoryCount || 0) !== 1 ? 's' : '' }` }>
                                        <Badge variant={ isMobile ? 'dot' : 'standard' } badgeContent={ localUser?.inventoryCount || 0 } color="secondary" max={ 99 } sx={ { '& .MuiBadge-badge': { color: '#000' } } }>
                                            <Inventory
                                                aria-label="inventory"
                                                color='primary'
                                                sx={ { fontSize: { xs: 16, sm: 24 } } }
                                                onClick={ (e) => {
                                                    e.stopPropagation(); // Prevent accordion toggle

                                                    handleInventoryClick();
                                                } }
                                            />
                                        </Badge>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </div>
                    </AccordionSummary>
                ) }
            </Accordion >

            {/* MARK: Inventory Dialog */ }
            <Dialog
                fullScreen={ isMobile ? true : false }
                fullWidth={ true }
                maxWidth="lg"
                open={ inventoryDialogOpen }
                onClose={ () => {
                    if (importStatus !== 'running' && importStatus !== 'paused') {
                        setInventoryDialogOpen(false);
                    }
                } }
            >
                <DialogTitle>{ localUser.displayName || 'User' }&apos;s Items</DialogTitle>

                <IconButton
                    aria-label="close"
                    onClick={ () => setInventoryDialogOpen(false) }
                    sx={ (theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[ 500 ],
                    }) }
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent sx={ { p: { xs: 1, sm: 2 } } }>
                    <FormControl sx={ { mb: 2, display: claims?.admin ? 'block' : 'none' } }>
                        <Typography variant="body2" sx={ { mb: 1 } }>Import Items</Typography>

                        <Button
                            component="label"
                            role={ undefined }
                            variant="contained"
                            tabIndex={ -1 }
                            startIcon={ <CloudUpload /> }
                            sx={ { mb: 2 } }
                        >
                            Upload file

                            <VisuallyHiddenInput
                                type="file"
                                onChange={ handleFileChange }
                                multiple
                            />
                        </Button>

                        {/* display uploaded file name */ }
                        <Typography variant="body2" sx={ { mt: 1 } }>
                            { selectedFiles.length > 0
                                ? `${ selectedFiles.length } file${ selectedFiles.length > 1 ? 's' : '' } selected: ${ selectedFiles.map(f => f.name).join(', ') }`
                                : 'No file selected'
                            }
                        </Typography>

                        { selectedFiles.length > 0 && <Typography variant="body2" sx={ { mt: 1 } }>
                            Total items to import: { importCount }
                        </Typography> }

                        { selectedFiles.length > 0 && (
                            <Box sx={ { mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' } }>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={ handleImport }
                                    disabled={ importStatus === 'running' || importStatus === 'paused' }
                                >
                                    Import
                                </Button>

                                { importStatus === 'running' && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={ handlePauseImport }
                                        >
                                            Pause
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={ handleStopImport }
                                        >
                                            Stop
                                        </Button>
                                    </>
                                ) }

                                { importStatus === 'paused' && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            onClick={ handleResumeImport }
                                        >
                                            Resume
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={ handleStopImport }
                                        >
                                            Stop
                                        </Button>
                                    </>
                                ) }
                            </Box>
                        ) }

                        { importProgress && (
                            <Box sx={ { mt: 2 } }>
                                <Typography variant="body2">
                                    { importProgress.status }: { importProgress.current } / { importProgress.total }
                                </Typography>

                                <LinearProgress
                                    variant="determinate"
                                    value={ (importProgress.current / importProgress.total) * 100 }
                                />
                            </Box>
                        ) }

                    </FormControl>

                    <Items
                        user={ user }
                        items={ userItems }
                        myList={ false }
                        readOnly={ true }
                        showVerify={ true }
                        selectedItems={ selectedItems }
                        onCancel={ () => setUserItems(prev => prev.filter(i => !isNewItem(i.id))) }
                        onNewItem={ handleNewItem }
                        onSelectItem={ handleSelectItem }
                        onSelectAll={ handleSelectAll }
                        onVerifyItems={ handleVerifySelected }
                        onSellerPayout={ handleSellerPayout }
                        onSaveItem={ handleSaveItem }
                        onRemoveItems={ handleRemoveItems }
                        verifying={ verifying }
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};